import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import MainLayout from '../../components/layouts/MainLayout';
import PublicLayout from '../../components/layouts/PublicLayout';
import ImageGallery from '../../components/common/ImageGallery';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import WishlistButton from '../../components/wishlist/WishlistButton';
import FollowButton from '../../components/seller/FollowButton';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { productService } from '../../services/product.service';
import { useCart } from '../../hooks/useCart';
import { useToast } from '../../components/common/Toast';
import { theme } from '../../config/theme';
import { formatProductPrice, getStockStatus, getDiscountText } from '../../utils/productHelpers';
import { ArrowLeftIcon } from '../../components/icons/Icons';

const ProductDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, isCustomer } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart, items, updateQuantity, loading: cartLoading } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await productService.getBySlug(slug);
      setProduct(response.data.product);
    } catch (error) {
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to add items to cart');
      navigate('/login');
      return;
    }
    await addToCart(product.id, quantity);
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!product) return null;

  const stockStatus = getStockStatus(product);
  const discountText = getDiscountText(product);
  
  const cartItem = items?.find(item => item.product?.id === product.id);

  const content = (
    <div style={{ padding: theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
      <Button
        variant="ghost"
        onClick={() => navigate('/products')}
        style={{ marginBottom: theme.spacing.lg }}
      >
        <ArrowLeftIcon size={18} />
        Back to Products
      </Button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing['2xl'] }}>
        <ImageGallery images={product.images} altText={product.name} />

        <div>
          <h1 style={{
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            marginBottom: theme.spacing.md,
          }}>
            {product.name}
          </h1>

          {product.category && (
            <div style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text.secondary,
              marginBottom: theme.spacing.md,
            }}>
              {product.category.name}
            </div>
          )}

          <div style={{ marginBottom: theme.spacing.lg }}>
            <Badge variant={stockStatus.color === 'success' ? 'success' : stockStatus.color === 'warning' ? 'warning' : 'error'}>
              {stockStatus.label}
            </Badge>
            {product.condition && (
              <Badge
                variant={product.condition === 'new' ? 'primary' : 'warning'}
                style={{ marginLeft: theme.spacing.sm }}
              >
                {product.condition === 'new' ? '✨ New' : '🔄 Used'}
              </Badge>
            )}
            {discountText && (
              <Badge variant="error" style={{ marginLeft: theme.spacing.sm }}>
                {discountText}
              </Badge>
            )}
          </div>

          <div style={{ marginBottom: theme.spacing.xl }}>
            <div style={{
              fontSize: theme.typography.fontSize['3xl'],
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.primary.main,
              marginBottom: theme.spacing.xs,
            }}>
              {formatProductPrice(product.price)}
            </div>
            {product.compare_price && (
              <div style={{
                fontSize: theme.typography.fontSize.lg,
                color: theme.colors.text.secondary,
                textDecoration: 'line-through',
              }}>
                {formatProductPrice(product.compare_price)}
              </div>
            )}
          </div>

          {!cartItem && isAuthenticated && isCustomer && (
            <div style={{ marginBottom: theme.spacing.xl }}>
              <label style={{
                display: 'block',
                marginBottom: theme.spacing.sm,
                fontWeight: theme.typography.fontWeight.medium,
              }}>
                Quantity
              </label>
              <input
                type="number"
                min="1"
                max={product.quantity || 999}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                style={{
                  width: '100px',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  border: `1px solid ${theme.colors.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: theme.typography.fontSize.base,
                }}
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: theme.spacing.md, marginBottom: theme.spacing.xl }}>
            {isAuthenticated && isCustomer ? (
              cartItem ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, flex: 1 }}>
                    <Button 
                      variant="outline"
                      size="lg"
                      onClick={() => updateQuantity(cartItem.id, Math.max(1, cartItem.quantity - 1))}
                      disabled={cartLoading}
                      style={{ width: '48px', padding: 0, justifyContent: 'center' }}
                    >
                      -
                    </Button>
                    <span style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, minWidth: '40px', textAlign: 'center' }}>
                      {cartItem.quantity}
                    </span>
                    <Button 
                      variant="primary"
                      size="lg"
                      onClick={() => updateQuantity(cartItem.id, cartItem.quantity + 1)}
                      disabled={cartLoading || stockStatus.status === 'out_of_stock'}
                      style={{ width: '48px', padding: 0, justifyContent: 'center' }}
                    >
                      +
                    </Button>
                  </div>
                  <WishlistButton productId={product.id} variant="button" size="lg" />
                </>
              ) : (
                <>
                  <Button
                    fullWidth
                    size="lg"
                    disabled={stockStatus.status === 'out_of_stock' || cartLoading}
                    loading={cartLoading}
                    onClick={handleAddToCart}
                  >
                    {cartLoading ? 'Adding...' : 'Add to Cart'}
                  </Button>
                  <WishlistButton productId={product.id} variant="button" size="lg" />
                </>
              )
            ) : (
              <Button fullWidth size="lg" onClick={() => navigate('/register')}>
                Sign up to Buy
              </Button>
            )}
          </div>

          {product.description && (
            <div style={{
              marginTop: theme.spacing.xl,
              paddingTop: theme.spacing.xl,
              borderTop: `1px solid ${theme.colors.gray[200]}`,
            }}>
              <h3 style={{
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.semibold,
                marginBottom: theme.spacing.md,
              }}>
                Description
              </h3>
              <p style={{ lineHeight: 1.6, color: theme.colors.text.secondary }}>
                {product.description}
              </p>
            </div>
          )}

          {/* Seller info with Follow button */}
          {product.seller && (
            <div style={{
              marginTop: theme.spacing.lg,
              padding: theme.spacing.lg,
              backgroundColor: theme.colors.gray[50],
              borderRadius: theme.borderRadius.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: theme.spacing.md,
            }}>
              <div>
                <div style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.colors.text.secondary,
                  marginBottom: theme.spacing.xs,
                }}>
                  Sold by
                </div>
                <div style={{ fontWeight: theme.typography.fontWeight.semibold }}>
                  {product.seller.store_name}
                </div>
              </div>
              <FollowButton sellerId={product.seller.id} size="sm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return isAuthenticated ? (
    <MainLayout>{content}</MainLayout>
  ) : (
    <PublicLayout>{content}</PublicLayout>
  );
};

export default ProductDetails;